import { CloseIcon, DownloadIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text as ChakraText,
  useDisclosure,
  useToast,
  VStack,
  Switch,
  FormLabel,
} from '@chakra-ui/react'
import { FixedSizeList as List } from 'react-window'
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import Papa from 'papaparse'
import { useTranslation } from 'react-i18next'
import {
  colors,
  IKReactWindowedSelect as IKReactSelect,
} from '../../../../ui-kit'
import { useEffectSkipInitial } from '../../../hooks/useEffectSkipInitial'

enum OptionType {
  String = 'String',
  Number = 'Number',
  Boolean = 'Boolean',
}

const getInitialValue = (optionType: OptionType): boolean | string => {
  if (optionType === OptionType.Boolean) {
    return true
  }
  return ''
}

interface Metadata {
  optionType: OptionType
  value: string | boolean | number
  error: string
}

interface CsvRowData {
  type: string
  value: string
  error?: string
  warning?: string
  lineNumber: number
}

const OptionTypeSelectMenu = ({
  fieldsMetadata,
  index,
  setFieldsMetadata,
}): React.ReactElement => {
  const metadata = fieldsMetadata[index]

  const handleOptionTypeChange = (newValue: { value: OptionType }) => {
    setFieldsMetadata(prev => {
      const updated = [...prev]
      updated[index].optionType = newValue.value
      updated[index].value = getInitialValue(newValue.value) // Use the NEW option type
      return updated
    })
  }

  return (
    <IKReactSelect
      menuPlacement="auto"
      variant="compact"
      captureMenuScroll={false}
      value={{ label: metadata.optionType, value: metadata.optionType }}
      onChange={handleOptionTypeChange}
      options={[
        { label: OptionType.String, value: OptionType.String },
        { label: OptionType.Number, value: OptionType.Number },
        { label: OptionType.Boolean, value: OptionType.Boolean },
      ]}
    />
  )
}

const OptionValueField = ({
  fieldsMetadata,
  index,
  setFieldsMetadata,
}): React.ReactElement => {
  const metadata = fieldsMetadata[index]

  const handleValueChange = useCallback(
    (newValue: string | number | boolean) => {
      setFieldsMetadata(prev => {
        const updated = [...prev]
        updated[index].value = newValue
        return updated
      })
    },
    [index, setFieldsMetadata],
  )

  const Component = useMemo(() => {
    switch (metadata.optionType) {
      case OptionType.String:
        return (
          <Input
            value={metadata.value as string}
            onChange={e => handleValueChange(e.target.value)}
          />
        )
      case OptionType.Number:
        return (
          <Input
            value={metadata.value as number}
            type="number"
            onChange={e => {
              handleValueChange(
                e.target.value !== '' ? Number(e.target.value) : '',
              )
            }}
          />
        )
      case OptionType.Boolean:
        return (
          <IKReactSelect
            menuPlacement="auto"
            variant="compact"
            value={{
              label: String(metadata.value),
              value: metadata.value,
            }}
            options={[
              { label: 'true', value: true },
              { label: 'false', value: false },
            ]}
            onChange={newValue => {
              handleValueChange(newValue.value)
            }}
          />
        )
      default:
        return <></>
    }
  }, [metadata.optionType, metadata.value, handleValueChange])

  return (
    <HStack width="100%">
      <Box flex="2 0">
        <OptionTypeSelectMenu
          fieldsMetadata={fieldsMetadata}
          index={index}
          setFieldsMetadata={setFieldsMetadata}
        />
      </Box>
      <Box flex="4 0">
        <FormControl isInvalid={metadata.error !== ''}>
          {Component}
          <FormErrorMessage>{metadata.error}</FormErrorMessage>
        </FormControl>
      </Box>
    </HStack>
  )
}

const SelectOptionsField = ({ field, meta, form }): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [hideValidRows, setHideValidRows] = useState(false)
  const [csvData, setCsvData] = useState<CsvRowData[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  const [fieldsMetadata, setFieldsMetadata] = useState<Metadata[]>(
    field?.value?.map(val => {
      let optionType: OptionType = OptionType.String
      if (typeof val === 'number') optionType = OptionType.Number
      else if (typeof val === 'boolean') optionType = OptionType.Boolean

      return {
        optionType,
        value: val,
        error: '',
      }
    }) ?? [],
  )

  // Clear metadata on schema type change (skips initial render)
  useEffectSkipInitial(() => {
    setFieldsMetadata([])
  }, [form.values.schema.type])

  // Sync `fieldsMetadata` changes back to Formik’s selectOptions + defaultValue
  useEffectSkipInitial(() => {
    // Filter out empty or undefined
    const newSelectOptions = fieldsMetadata
      .filter(md => md.value !== '' && md.value !== undefined)
      .map(md => md.value)

    // If defaultValue is no longer valid, unset it
    const defaultVal = form.values?.schema?.defaultValue
    const unsetDefaultValue = Array.isArray(defaultVal)
      ? !defaultVal.every((val: any) => newSelectOptions.includes(val))
      : !newSelectOptions.includes(defaultVal)

    if (unsetDefaultValue) {
      form.setValues({
        ...form.values,
        schema: {
          ...form.values.schema,
          selectOptions: newSelectOptions,
          defaultValue: undefined,
        },
      })
    } else {
      form.setFieldValue('schema.selectOptions', newSelectOptions)
    }
  }, [fieldsMetadata, form.values?.schema?.defaultValue, form.setFieldValue])

  const handleAddOption = () => {
    setFieldsMetadata(prev => [
      ...prev,
      { optionType: OptionType.String, value: '', error: '' },
    ])
  }

  const handleRemoveOption = (removeIndex: number) => {
    setFieldsMetadata(prev => prev.filter((_, idx) => idx !== removeIndex))
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    Papa.parse(file, {
      header: true,
      skipEmptyLines: false,
      complete: (results: any) => {
        try {
          const csvRowData: CsvRowData[] = []
          const validationErrors: string[] = []

          // Validate headers
          const requiredHeaders = ['Type', 'Value']
          const headers = results.meta.fields || []
          if (!requiredHeaders.every(h => headers.includes(h))) {
            throw new Error(
              'Missing or invalid headers. Expected: "Type", "Value".',
            )
          }

          // Validate for empty file
          if (results.data.length === 0) {
            throw new Error('CSV file cannot be blank.')
          }

          const newMetadata: Metadata[] = []

          // Process each row
          results.data.forEach((row: any, rowIndex: number) => {
            // Skip empty rows but track them
            if (!row.Type && !row.Value) {
              csvRowData.push({
                type: '',
                value: '',
                error: 'Blank row detected',
                lineNumber: rowIndex + 2,
              })
              return
            }

            // Validate for blank fields
            const rowData: CsvRowData = {
              type: row.Type || '',
              value: row.Value || '',
              lineNumber: rowIndex + 2,
            }

            if (!row.Type || !row.Value) {
              rowData.error = 'Missing required fields (Type, Value)'
              csvRowData.push(rowData)
              return
            }

            const rawType = row.Type.trim()
            const rawValue = row.Value.toString().trim()

            // Check for Excel errors
            if (
              [
                '######',
                '#VALUE!',
                '#DIV/0!',
                '#NAME?',
                '#N/A',
                '#REF!',
                '#NUM!',
                '#NULL!',
                '#SPILL!',
                '#CALC!',
              ].includes(rawValue)
            ) {
              rowData.warning = `Excel error detected - ${rawValue}`
            }

            // Validate Type case sensitivity and content
            if (!/^(string|number|boolean)$/.test(rawType)) {
              rowData.error = `"Type" column must contain only: string, number, or boolean.`
              csvRowData.push(rowData)
              return
            }

            let optionType = OptionType.String
            let value: any = rawValue

            // Type-specific validations
            switch (rawType) {
              case 'number':
                optionType = OptionType.Number
                if (!/^-?\d+(\.\d+)?$/.test(rawValue)) {
                  rowData.error =
                    '"Number" field must contain digits only (0-9).'
                  csvRowData.push(rowData)
                  return
                }
                value = Number(rawValue)
                break

              case 'boolean':
                optionType = OptionType.Boolean
                if (!['true', 'false'].includes(rawValue.toLowerCase())) {
                  rowData.error =
                    '"Boolean" field must be either "True" or "False".'
                  csvRowData.push(rowData)
                  return
                }
                value = rawValue.toLowerCase() === 'true'
                break

              case 'string':
                optionType = OptionType.String
                break
            }

            newMetadata.push({
              optionType,
              value,
              error: '',
            })

            // Add successful row to csvRowData
            csvRowData.push(rowData)
          })

          // Remove 'Blank row detected' errors if they are at the very end of csvRowData
          while (
            csvRowData.length > 0 &&
            csvRowData[csvRowData.length - 1].error === 'Blank row detected'
          ) {
            csvRowData.pop();
          }

          const validRows = csvRowData.filter(r => !r.error && !r.warning)
          const rawVals = validRows.map(r => r.value.toString().trim())
          const dupVals = rawVals.filter((v, i) => rawVals.indexOf(v) !== i)
          const uniqueDup = Array.from(new Set(dupVals))

          if (uniqueDup.length > 0) {
            const updated = csvRowData.map(row => {
              const val = row.value.toString().trim()
              if (!row.error && !row.warning && uniqueDup.includes(val)) {
                return { ...row, error: 'Must not have duplicate values.' }
              }
              return row
            })

            setCsvData(updated)

            toast({
              title: 'Duplicate values found',
              description: (
                <Box as="button" onClick={onOpen} textAlign="left" width="100%">
                  {uniqueDup.length === 1
                    ? `There was a duplicate value in the CSV file: "${uniqueDup[0]}". Click here to view details.`
                    : `There were ${
                        uniqueDup.length
                      } duplicate values in the CSV file. Click here to view details.`}
                </Box>
              ),
              status: 'error',
              isClosable: true,
            })

            return
          }

          // Show validation errors and warnings if any
          const hasErrors = csvRowData.some(row => row.error)
          const hasWarnings = csvRowData.some(row => row.warning)

          if (hasErrors || hasWarnings) {
            setCsvData(csvRowData)

            if (hasErrors) {
              throw {
                message: 'CSV validation failed',
                errorCount: csvRowData.filter(row => row.error).length,
                warningCount: csvRowData.filter(row => row.warning).length,
              }
            } else if (hasWarnings) {
              toast({
                title: 'Warning',
                description: (
                  <Box
                    as="button"
                    onClick={onOpen}
                    textAlign="left"
                    width="100%"
                  >
                    {csvRowData.filter(row => row.warning).length === 1
                      ? 'There was a warning in the CSV file. Click here to view details.'
                      : `There were ${
                          csvRowData.filter(row => row.warning).length
                        } warnings in the CSV file. Click here to view details.`}
                  </Box>
                ),
                status: 'warning',
                isClosable: true,
              })
            }
          }

          // Require at least one valid option
          if (newMetadata.length === 0) {
            throw new Error('No valid options found in the CSV file')
          }

          setFieldsMetadata(newMetadata)
          toast({
            title: t('success'),
            description: `Successfully imported ${newMetadata.length} options`,
            status: 'success',
          })
        } catch (error: any) {
          if (error.errorCount) {
            toast({
              title: error.message ?? t('error'),
              description: (
                <Box as="button" onClick={onOpen} textAlign="left" width="100%">
                  {error.errorCount === 1
                    ? 'There was an error in the CSV file. The data has not been imported. Click here to view details.'
                    : `There were ${error.errorCount} errors in the CSV file. The data has not been imported. Click here to view details.`}
                </Box>
              ),
              status: 'error',
              isClosable: true,
            })
          } else {
            toast({
              title: t('error'),
              description:
                error.message ??
                t('unknown_error', 'An unknown error occurred'),
              status: 'error',
            })
          }
        }
      },
      error: (error: any) => {
        toast({
          title: t('error'),
          description:
            error?.message || t('unknown_error', 'An unknown error occurred'),
          status: 'error',
        })
      },
    })

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleDownloadTemplate = () => {
    const csvContent = 'Type,Value\nstring,example\nnumber,42\nboolean,true'
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'metadata_template.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const handleDownloadCurrent = () => {
    const csvContent =
      'Type,Value\n' +
      fieldsMetadata
        .map(md => `${md.optionType.toLowerCase()},${md.value}`)
        .join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'metadata_options.csv'
    a.click()
    window.URL.revokeObjectURL(url)
  }

  return (
    <Tabs width="full" index={tabIndex} onChange={setTabIndex}>
      <TabList>
        <Tab>{t('manual_edit', 'Manual edit')}</Tab>
        <Tab>{t('csv_upload', 'CSV upload')}</Tab>
      </TabList>

      <TabPanels>
        <TabPanel padding={0} pt={4}>
          <VStack width="full" alignItems="flex-start">
            {fieldsMetadata.length > 100 ? (
              <Box
                width="full"
                p={4}
                bg="red.50"
                borderRadius="md"
                border="1px solid"
                borderColor="red.200"
              >
                <VStack spacing={2} align="flex-start">
                  <ChakraText color="red.600" fontWeight="medium">
                    {t(
                      'too_many_options_manual',
                      'Too many options for manual editing',
                    )}
                  </ChakraText>
                  <ChakraText color="red.600">
                    {t('use_csv_upload_instead', 'Please use')}{' '}
                    <ChakraText
                      as="span"
                      color="blue.500"
                      cursor="pointer"
                      textDecoration="underline"
                      onClick={() => setTabIndex(1)}
                    >
                      {t('csv_upload', 'CSV Upload')}
                    </ChakraText>{' '}
                    {t('to_manage_options', 'to manage more than 100 options.')}
                  </ChakraText>
                </VStack>
              </Box>
            ) : (
              <>
                {fieldsMetadata.map((_, index) => (
                  <HStack width="full" key={index}>
                    <OptionValueField
                      fieldsMetadata={fieldsMetadata}
                      index={index}
                      setFieldsMetadata={setFieldsMetadata}
                    />
                    <CloseIcon
                      cursor="pointer"
                      boxSize="0.825rem"
                      color={colors.appBattleshipGrey}
                      onClick={() => handleRemoveOption(index)}
                    />
                  </HStack>
                ))}
                <ChakraText
                  color={colors.appClearBlue}
                  fontWeight="medium"
                  cursor="pointer"
                  onClick={handleAddOption}
                >
                  {t('plus_create_an_option', '+ Create an option')}
                </ChakraText>
              </>
            )}
          </VStack>
        </TabPanel>

        <TabPanel padding={0} pt={4}>
          <VStack spacing={4} width="full" align="flex-start">
            <HStack width="full" spacing={4}>
              <Button
                leftIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
                size="sm"
                variant="outline"
              >
                {t('download_template', 'Download template')}
              </Button>
              <Button
                leftIcon={<DownloadIcon />}
                onClick={handleDownloadCurrent}
                size="sm"
                variant="outline"
                isDisabled={fieldsMetadata.length === 0}
              >
                {t('download_current', 'Download current')}
              </Button>
            </HStack>

            <Box
              width="full"
              p={6}
              border="2px dashed"
              borderColor={isDragging ? 'blue.400' : 'gray.200'}
              borderRadius="md"
              textAlign="center"
              transition="all 0.2s"
              bg={isDragging ? 'blue.50' : 'transparent'}
              onDragOver={e => {
                e.preventDefault()
                e.stopPropagation()
                setIsDragging(true)
              }}
              onDragEnter={e => {
                e.preventDefault()
                e.stopPropagation()
                setIsDragging(true)
              }}
              onDragLeave={e => {
                e.preventDefault()
                e.stopPropagation()
                setIsDragging(false)
              }}
              onDrop={e => {
                e.preventDefault()
                e.stopPropagation()
                setIsDragging(false)

                const files = Array.from(e.dataTransfer.files)
                const csvFile = files.find(file => file.name.endsWith('.csv'))

                if (!csvFile) {
                  toast({
                    title: t('error'),
                    description: t(
                      'invalid_file_type',
                      'Please upload a CSV file',
                    ),
                    status: 'error',
                  })
                  return
                }

                // Trigger the file upload handler
                const dataTransfer = new DataTransfer()
                dataTransfer.items.add(csvFile)
                if (fileInputRef.current) {
                  fileInputRef.current.files = dataTransfer.files
                  const event = new Event('change', { bubbles: true })
                  fileInputRef.current.dispatchEvent(event)
                }
              }}
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              <VStack spacing={2}>
                <ChakraText>
                  {t('drop_csv', 'Drop your CSV file here or')}
                </ChakraText>
                <Button size="sm" onClick={() => fileInputRef.current?.click()}>
                  {t('browse_files', 'Browse files')}
                </Button>
                <ChakraText fontSize="sm" color="gray.500">
                  {t(
                    'csv_format',
                    'CSV format: Type (string/number/boolean), Value',
                  )}
                </ChakraText>
              </VStack>
            </Box>
          </VStack>
        </TabPanel>
      </TabPanels>
      <Modal isOpen={isOpen} onClose={onClose} size="5xl">
        <ModalOverlay />
        <ModalContent maxH="80vh" w="80vw">
          <ModalHeader
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            {t('csv_validation_status', 'CSV Validation Status')}
            <FormControl display="flex" alignItems="center" maxW="200px">
              <Switch
                id="hide-valid"
                size="sm"
                mr={2}
                onChange={e => setHideValidRows(e.target.checked)}
                isChecked={hideValidRows}
              />
              <FormLabel htmlFor="hide-valid" mb={0} fontSize="sm">
                {t('hide_valid_rows', 'Hide valid rows')}
              </FormLabel>
            </FormControl>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box>
              <Box
                borderBottom="1px solid"
                borderColor="gray.200"
                display="flex"
                alignItems="center"
              >
                <Box
                  width="120px"
                  px={3}
                  py={3}
                  fontSize="md"
                  lineHeight="4"
                  letterSpacing="wider"
                  textTransform="uppercase"
                  color="gray.600"
                  fontWeight="bold"
                >
                  {t('row', 'Row')}
                </Box>
                <Box
                  width="150px"
                  px={3}
                  py={3}
                  fontSize="md"
                  lineHeight="4"
                  letterSpacing="wider"
                  textTransform="uppercase"
                  color="gray.600"
                  fontWeight="bold"
                >
                  {t('type', 'Type')}
                </Box>
                <Box
                  width="200px"
                  px={3}
                  py={3}
                  fontSize="md"
                  lineHeight="4"
                  letterSpacing="wider"
                  textTransform="uppercase"
                  color="gray.600"
                  fontWeight="bold"
                >
                  {t('value', 'Value')}
                </Box>
                <Box
                  flex="1"
                  width="300px"
                  px={3}
                  py={3}
                  fontSize="md"
                  lineHeight="4"
                  letterSpacing="wider"
                  textTransform="uppercase"
                  color="gray.600"
                  fontWeight="bold"
                  whiteSpace="nowrap"
                >
                  {t('status', 'Status')}
                </Box>
              </Box>
              <Box height="calc(80vh - 200px)">
                <List
                  height={Math.min(
                    csvData.length * 53,
                    window.innerHeight * 0.8 - 200,
                  )}
                  itemCount={
                    hideValidRows
                      ? csvData.filter(row => row.error || row.warning).length
                      : csvData.length
                  }
                  itemSize={53}
                  width="100%"
                >
                  {({ index, style }) => {
                    const row = hideValidRows
                      ? csvData.filter(row => row.error || row.warning)[index]
                      : csvData[index]
                    return (
                      <Box
                        style={style}
                        bg={
                          row.error
                            ? 'red.50'
                            : row.warning
                            ? 'yellow.50'
                            : 'transparent'
                        }
                        borderBottom="1px solid"
                        borderColor="gray.200"
                        display="flex"
                        alignItems="center"
                      >
                        <Box width="120px" px={3} py={3}>
                          {row.lineNumber}
                        </Box>
                        <Box width="150px" px={3} py={3}>
                          {row.type}
                        </Box>
                        <Box width="200px" px={3} py={3}>
                          {row.value}
                        </Box>
                        <Box
                          flex="1"
                          width="300px"
                          px={3}
                          py={3}
                          color={
                            row.error
                              ? 'red.600'
                              : row.warning
                              ? 'orange.600'
                              : 'green.600'
                          }
                          whiteSpace="nowrap"
                        >
                          {row.error || row.warning || t('valid', 'Valid')}
                        </Box>
                      </Box>
                    )
                  }}
                </List>
              </Box>
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Tabs>
  )
}

export default SelectOptionsField
